import { PureComponent, ReactNode } from 'react';
import { GO_TO_HE_DASHBOARD } from '../../routes/paths';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Order } from '../../api/fetchAllHEOrder';
import {
    MISC_KEYS,
    getActiveOrderUpdates,
    getMySingleDoctorId,
    updateItemInMisc,
} from '../../store/idbHandler';
import { captureError } from '../../pages/captureError';
import { GetMedicineOrderStatus } from '../prescriptionPad/rightPanel/finishScreen/OrderMedicineUtils';
import moment from 'moment';
import { Data as MedicineData } from '../../api/getMedicinesOrder';
import { Data as LabData } from '../../api/getLabTestsOrder';
import { GetLabOrderStatus } from '../prescriptionPad/rightPanel/finishScreen/BookLabTestsUtils';
import { ActiveOrderUpdatesCount } from '../../constants';
import { track } from '../../tracking';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../../store/reducers';
import { Actions } from '../../store/actions';
import { setActiveOrderUpdatesCount } from '../../store/actions/session';
import { ACTIVE_TAB } from '../../pages/HEDashboard';
import { GetCurrentOrderStatus } from '../../pages/HEDashboardUtils';

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    viewOrder: (order?: Order) => void;
}

interface State {
    isLoading: boolean;
    data: Order[];
}

class ActiveOrderUpdates extends PureComponent<Props, State> {
    state: State = {
        isLoading: true,
        data: [],
    };

    componentDidMount = async (): Promise<void> => {
        const { getAllActiveOrderUpdates, resetActiveOrderUpdatesCount } = this;
        getAllActiveOrderUpdates();
        resetActiveOrderUpdatesCount();
    };

    resetActiveOrderUpdatesCount = async (): Promise<void> => {
        const { dispatch } = this.props;
        try {
            await updateItemInMisc<ActiveOrderUpdatesCount>(
                getMySingleDoctorId() || '',
                MISC_KEYS.activeOrderUpdatesCount,
                {
                    count: 0,
                },
            );
            dispatch(setActiveOrderUpdatesCount(0));
        } catch (error) {
            captureError(error as Error);
        }
    };

    onHEDashboard = (): void => {
        const { history } = this.props;
        history.push(GO_TO_HE_DASHBOARD('not-ordered'));
        track('ACTIVE_ORDER_POPUP_VIEW_ALL_CLICK');
    };

    getAllActiveOrderUpdates = async (): Promise<void> => {
        try {
            const orders = await getActiveOrderUpdates();
            if (Array.isArray(orders)) {
                this.setState({
                    data: orders,
                });
                track('ACTIVE_ORDER_UPDATES_POPOVER_MOUNT', { orders: String(orders.length) });
            }
        } catch (error) {
            captureError(error as Error);
        } finally {
            this.setState({
                isLoading: false,
            });
        }
    };

    render(): ReactNode {
        const { viewOrder } = this.props;
        const { isLoading, data } = this.state;
        const { onHEDashboard } = this;

        return (
            <div className="flex flex-col w-360 h-420 border border-darwin-neutral_new-200 rounded-8 overflow-hidden">
                <div className="p-16 bg-darwin-neutrals-50 border-b border-darwin-neutral_new-200 flex items-center justify-between">
                    <p className="text-16 font-700">Order updates</p>
                    <button className="flex items-center space-x-8" onClick={onHEDashboard}>
                        <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                            View all
                        </p>
                        <p>
                            <svg
                                className="w-12 h-12 fill-darwin-accent-bright-blue-800"
                                viewBox="0 0 12 11"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M11.4844 5.58203L7.01562 9.84766C6.76172 10.0762 6.38086 10.0762 6.15234 9.82227C5.92383 9.56836 5.92383 9.1875 6.17773 8.95898L9.55469 5.73438H0.921875C0.566406 5.73438 0.3125 5.48047 0.3125 5.125C0.3125 4.79492 0.566406 4.51562 0.921875 4.51562H9.55469L6.17773 1.31641C5.92383 1.08789 5.92383 0.681641 6.15234 0.453125C6.38086 0.199219 6.78711 0.199219 7.01562 0.427734L11.4844 4.69336C11.6113 4.82031 11.6875 4.97266 11.6875 5.125C11.6875 5.30273 11.6113 5.45508 11.4844 5.58203Z" />
                            </svg>
                        </p>
                    </button>
                </div>
                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <svg
                            className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                        </svg>
                    </div>
                ) : data.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                        <p>No order updates found!</p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-auto">
                        {data.map((i) => (
                            <button
                                className="px-16 py-12 border-b border-darwin-neutral_new-200 space-y-6 hover:bg-darwin-neutrals-50 w-full duration-300"
                                onClick={() => viewOrder(i)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-8">
                                        {i.type === ACTIVE_TAB.MEDS ? (
                                            <p>
                                                <svg
                                                    className="w-16 h-16 fill-darwin-neutrals-600"
                                                    viewBox="0 0 16 16"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M3.53125 4.0625C2.8457 4.0625 2.3125 4.62109 2.3125 5.28125V8.125H4.75V5.28125C4.75 4.62109 4.19141 4.0625 3.53125 4.0625ZM0.6875 5.28125C0.6875 3.73242 1.95703 2.4375 3.53125 2.4375C5.08008 2.4375 6.375 3.73242 6.375 5.28125V10.9688C6.375 12.543 5.08008 13.8125 3.53125 13.8125C1.95703 13.8125 0.6875 12.543 0.6875 10.9688V5.28125ZM14.7539 11.7812C14.5762 12.0859 14.1699 12.1113 13.916 11.8574L9.14258 7.08398C8.88867 6.83008 8.91406 6.42383 9.21875 6.24609C9.82812 5.89062 10.5137 5.6875 11.25 5.6875C13.4844 5.6875 15.3125 7.51562 15.3125 9.75C15.3125 10.4863 15.1094 11.1719 14.7539 11.7812ZM13.2559 13.2793C12.6719 13.6348 11.9609 13.8125 11.25 13.8125C8.99023 13.8125 7.1875 12.0098 7.1875 9.75C7.1875 9.03906 7.36523 8.32812 7.7207 7.74414C7.89844 7.43945 8.30469 7.41406 8.55859 7.66797L13.332 12.4414C13.5859 12.6953 13.5605 13.1016 13.2559 13.2793Z" />
                                                </svg>
                                            </p>
                                        ) : i.type === ACTIVE_TAB.LABS ? (
                                            <p>
                                                <svg
                                                    className="w-14 h-14 fill-darwin-neutrals-600"
                                                    viewBox="0 0 14 14"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M9.18359 0.878906L9.99609 1.69141L12.4336 4.12891L13.2461 4.94141C13.5762 5.24609 13.5762 5.7793 13.2461 6.08398C12.9414 6.41406 12.4082 6.41406 12.1035 6.08398L11.875 5.85547L4.79102 12.9141C4.33398 13.3711 3.72461 13.625 3.06445 13.625H2.9375C1.5918 13.625 0.5 12.5332 0.5 11.1875V11.0605C0.5 10.4004 0.753906 9.79102 1.21094 9.33398L8.26953 2.27539L8.04102 2.02148C7.71094 1.7168 7.71094 1.18359 8.04102 0.878906C8.3457 0.548828 8.87891 0.548828 9.18359 0.878906ZM5.70508 7.125H8.26953L10.707 4.6875L9.4375 3.41797L5.70508 7.125Z" />
                                                </svg>
                                            </p>
                                        ) : null}
                                        <p
                                            className="font-700 truncate-overflow-1 text-left"
                                            style={{ color: '#1A1A1A' }}
                                        >
                                            {i.patient_name || 'Not available'}
                                        </p>
                                    </div>
                                    <div className="whitespace-nowrap flex items-center space-x-8 relative -right-6">
                                        {i.type === ACTIVE_TAB.MEDS ? (
                                            <GetMedicineOrderStatus
                                                order_details={(i as unknown) as MedicineData}
                                                removeBorder
                                                showTag
                                            />
                                        ) : i.type === ACTIVE_TAB.LABS ? (
                                            <GetLabOrderStatus
                                                order_details={(i as unknown) as LabData}
                                                removeBorder
                                                showTag
                                            />
                                        ) : null}
                                        <GetCurrentOrderStatus order_details={i} />
                                    </div>
                                </div>
                                {((i.external_remarks && i.external_remarks.length > 0) ||
                                    i.updated_at) && (
                                    <div className="flex items-center justify-between space-x-8">
                                        <div className="text-13 text-darwin-neutral_new-800 text-left">
                                            {i.external_remarks?.map((i) => (
                                                <p>{i}</p>
                                            ))}
                                        </div>
                                        <p className="text-10 font-700 text-darwin-neutrals-600 whitespace-nowrap">
                                            {i.updated_at && moment(i.updated_at).format('LLL')}
                                        </p>
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

export default connect()(withRouter(ActiveOrderUpdates));
